import React, { useState } from "react";
import { message } from "@centrate-io/barn";
import { useApolloClient } from "@apollo/client";
import { IconAt, IconDeviceMobile, IconLoader2 } from "@tabler/icons-react";
import { cpLookupAccount, cpSendVerification } from "_graphql/mutations";
import { usePortalSettings } from "_contexts/usePortalSettings";
import "./SignIn.scss";

// Background images
import FieldsImage from "_assets/images/customer-portal/fields.jpg";
import HillsImage from "_assets/images/customer-portal/hills.jpg";
import MountainsImage from "_assets/images/customer-portal/mountains.jpg";
import PoppiesImage from "_assets/images/customer-portal/poppies.jpg";
import SunsetImage from "_assets/images/customer-portal/sunset.jpg";

const backgroundImages = {
  fields: FieldsImage,
  hills: HillsImage,
  mountains: MountainsImage,
  poppies: PoppiesImage,
  sunset: SunsetImage,
};

function SignIn({ onVerificationSent }) {
  const client = useApolloClient();
  const { settings, companyId } = usePortalSettings();

  const [channel, setChannel] = useState("phone"); // 'phone' or 'email'
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);

  // Get company settings
  const logoUrl = settings?.logo_url;
  const companyName = settings?.company?.name || "Paypath";
  const enrollUrl = settings?.redirect_url;
  const backgroundImage = settings?.background_image || "poppies";

  // Get background image URL
  const bgImageUrl =
    backgroundImages[backgroundImage] || backgroundImages.poppies;

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
        6,
        10
      )}`;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (channel === "phone") {
      setIdentifier(formatPhoneNumber(value));
    } else {
      setIdentifier(value);
    }
  };

  const handleContinue = async () => {
    if (!identifier.trim()) {
      message.error(
        channel === "phone"
          ? "Please enter your phone number"
          : "Please enter your email address"
      );
      return;
    }

    setLoading(true);

    try {
      // First, lookup the account
      const lookupResult = await client.mutate({
        mutation: cpLookupAccount,
        variables: {
          company_id: companyId,
          identifier: identifier.trim(),
          channel: channel,
        },
      });

      const lookup = lookupResult?.data?.cpLookupAccount;

      if (!lookup?.success) {
        message.error(lookup?.error || "Account not found");
        setLoading(false);
        return;
      }

      // Send verification code
      const verifyChannel = channel === "phone" ? "sms" : "email";
      const sendResult = await client.mutate({
        mutation: cpSendVerification,
        variables: {
          application_id: lookup.application_id,
          channel: verifyChannel,
        },
      });

      const send = sendResult?.data?.cpSendVerification;

      if (!send?.success) {
        message.error(send?.error || "Failed to send verification code");
        setLoading(false);
        return;
      }

      // Navigate to verification screen
      onVerificationSent({
        applicationId: lookup.application_id,
        channel: verifyChannel,
        maskedDestination: send.masked_destination,
        phone: lookup.phone,
        email: lookup.email,
      });
    } catch (err) {
      console.error("SignIn error:", err);
      message.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && identifier.trim()) {
      handleContinue();
    }
  };

  const switchChannel = () => {
    setChannel(channel === "phone" ? "email" : "phone");
    setIdentifier("");
  };

  const isPhoneValid =
    channel === "phone" && identifier.replace(/\D/g, "").length >= 10;
  const isEmailValid =
    channel === "email" && identifier.includes("@") && identifier.includes(".");
  const isValid = channel === "phone" ? isPhoneValid : isEmailValid;

  return (
    <div className="auth-signin-container">
      {/* Background with blur - only visible on desktop */}
      <div
        className="auth-background"
        style={{ backgroundImage: `url(${bgImageUrl})` }}
      />

      {/* Modal on desktop, full screen on mobile */}
      <div className="auth-modal">
        <div className="auth-content">
          {/* Brand Icon */}
          {logoUrl && (
            <div className="brand-icon">
              <img src={logoUrl} alt={companyName} />
            </div>
          )}

          {/* Welcome Text */}
          <div className="welcome-section">
            <h1 className="welcome-title">
              Welcome Back!
              <br />
              Sign In Below
            </h1>
            <p className="welcome-subtitle">
              For returning clients, please enter your{" "}
              {channel === "phone" ? "phone number" : "email address"} to access
              your account.
            </p>
          </div>

          {/* Input Section */}
          <div className="input-section">
            <div className="input-container">
              <input
                type={channel === "email" ? "email" : "tel"}
                placeholder={
                  channel === "phone"
                    ? "Enter your phone number..."
                    : "Enter your email address..."
                }
                value={identifier}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete={channel === "phone" ? "tel" : "email"}
              />
            </div>

            <button
              className={`continue-button ${
                isValid && !loading ? "active" : ""
              }`}
              onClick={handleContinue}
              disabled={!isValid || loading}
            >
              {loading ? (
                <IconLoader2 className="loading-icon" size={20} />
              ) : (
                "Continue"
              )}
            </button>

            {/* OR Divider */}
            <div className="or-divider">
              <div className="divider-line" />
              <span className="divider-text">OR</span>
              <div className="divider-line" />
            </div>

            {/* Switch Channel Button */}
            <button className="switch-channel-button" onClick={switchChannel}>
              {channel === "phone" ? (
                <>
                  <IconAt size={18} />
                  <span>Sign in with Email</span>
                </>
              ) : (
                <>
                  <IconDeviceMobile size={18} />
                  <span>Sign in with Phone Number</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer Section */}

        {enrollUrl && (
          <div className="auth-footer">
            <div className="enroll-section">
              <span className="enroll-text">Don't have an account?</span>
              <a
                href={enrollUrl}
                className="enroll-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Enroll Today
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignIn;
